import { Controller } from "@hotwired/stimulus"
import { Turbo } from "@hotwired/turbo-rails"
import { syncPageScrollLock } from "../src/page_scroll_lock"
import { PAGE_HANDOFF_KEY, PAGE_RESULT_KEY, readStored, restoredByVisit } from "../src/search_picker_page"
import { triggerHaptic } from "../src/trigger_native_haptic"

// Matches the longest close transition in the picker stylesheet.
const CLOSE_TRANSITION_MS = 320

// crypto.randomUUID only exists in secure contexts, and dev native apps load over plain http.
let pickerCount = 0

// The field takes the select's place in the layout, so it keeps the select's
// sizing and spacing. Borders, padding and colors are left to the trigger.
const LAYOUT_CLASS = /^-?(?:w|min-w|max-w|m[trblxyse]?|basis|grow|shrink|flex-(?:1|auto|initial|none)|order|self|col-span|col-start|col-end|row-span)(?:-|$)/

// The select owns submitted values. The picker only edits its options, so Rails,
// native navigation guards and filter controllers all see ordinary form events.
export default class extends Controller {
  static values = {
    searchUrl: String,
    maxItems: { type: Number, default: 0 },
    sortField: String,
    navigate: Boolean,
    title: String,
    // Offered when a search finds nothing, e.g. a link to submit the missing item.
    // The typed search is appended as the `name` param.
    emptyActionLabel: String,
    emptyActionUrl: String,
    invalidMessage: { type: String, default: "Choose an option to continue." },
    // Native apps push this page inside the current modal instead of covering
    // it with the dialog (see openPage).
    pageUrl: String,
    // Renders the picker in the page rather than as a dialog, for that page.
    inline: Boolean
  }

  connect() {
    this.listeners = new AbortController()
    this.items = new Map()
    this.cache = new Map()
    this.originalHidden = this.element.hidden
    this.field = this.cloneTemplate("field")
    this.field.className = Array.from(this.element.classList).filter(name => LAYOUT_CLASS.test(name.split(":").pop())).join(" ")
    this.field.dataset.searchPickerField = ""
    this.trigger = this.field.querySelector("[data-picker-trigger]")
    this.chips = this.field.querySelector("[data-picker-chips]")
    this.dialog = this.cloneTemplate("dialog")
    this.search = this.dialog.querySelector("[data-picker-search]")
    this.results = this.dialog.querySelector("[data-picker-results]")
    this.skeleton = this.dialog.querySelector("[data-picker-skeleton]")
    this.empty = this.dialog.querySelector("[data-picker-empty]")
    this.selection = this.dialog.querySelector("[data-picker-selection]")
    this.hint = this.dialog.querySelector("[data-picker-hint]")
    this.searchClear = this.dialog.querySelector("[data-picker-search-clear]")
    this.status = this.dialog.querySelector("[data-picker-status]")
    this.clearButton = this.dialog.querySelector("[data-picker-clear]")
    this.retryButton = this.dialog.querySelector("[data-picker-retry]")
    this.footer = this.dialog.querySelector("[data-picker-footer]")
    this.doneButton = this.dialog.querySelector("[data-picker-done]")
    const title = this.dialog.querySelector("[data-picker-title]")
    title.id = `search-picker-${++pickerCount}`
    title.textContent = this.label
    this.dialog.setAttribute("aria-labelledby", title.id)
    this.dialog.id = `${title.id}-dialog`
    this.trigger.setAttribute("aria-controls", this.dialog.id)

    this.element.hidden = true
    if (this.inlineValue) {
      // The page itself is the picker, so the Android back button leaves it
      // rather than closing it as a dialog.
      this.dialog.removeAttribute("data-controller")
      this.dialog.dataset.inline = ""
      this.element.after(this.dialog)
    } else {
      this.element.after(this.field)
      document.body.append(this.dialog)
    }
    this.listen(this.trigger, "click", () => this.open())
    // Warm the default results so the list is ready when the dialog opens.
    for (const event of ["pointerenter", "focus", "touchstart"]) {
      this.listen(this.trigger, event, () => this.prefetch(), { passive: true })
    }
    this.listen(this.element, "change", () => this.refresh())
    this.listen(this.element, "search-picker:refresh", () => this.refresh())
    this.listen(this.element, "invalid", event => {
      event.preventDefault()
      this.open()
      this.updateStatus(this.invalidMessageValue)
    })
    this.listen(this.element, "focus", () => this.trigger.focus())
    for (const label of this.element.labels) {
      this.listen(label, "click", event => {
        if (event.target.closest("button, a, input")) return
        event.preventDefault()
        this.open()
      })
    }
    this.listen(this.search, "paste", event => this.pasted(event))
    this.listen(this.search, "input", () => {
      if (this.search.value.trim() !== this.pastedId) this.pastedId = null
      this.scheduleSearch()
    })
    this.listen(this.searchClear, "click", () => {
      this.search.value = ""
      this.search.focus()
      this.scheduleSearch()
    })
    this.listen(this.dialog, "keydown", event => this.keydown(event))
    this.listen(this.dialog, "cancel", event => {
      event.preventDefault()
      this.close()
    })
    this.listen(this.dialog, "click", event => {
      if (event.target === this.dialog || event.target.closest("[data-picker-close]")) this.close()
    })
    this.listen(this.clearButton, "click", () => this.clear())
    this.listen(this.retryButton, "click", () => this.loadResults())
    this.listen(this.results, "click", event => {
      const button = event.target.closest("[data-picker-value]")
      if (!button) return
      // Rows render after turbo:load, so the haptic class has no listener of its own.
      if (button.classList.contains("trigger-native-haptic")) triggerHaptic()
      this.choose(button.dataset.pickerValue)
    })
    this.listen(this.chips, "click", event => {
      const button = event.target.closest("[data-picker-value]")
      if (button) {
        this.remove(button.dataset.pickerValue)
        this.trigger.focus({ preventScroll: true })
      }
    })
    this.listen(this.selection, "click", event => {
      const button = event.target.closest("[data-picker-value]")
      if (button) {
        this.remove(button.dataset.pickerValue)
        if (this.prefersKeyboard) this.search.focus({ preventScroll: true })
      }
    })
    if (this.element.form) {
      this.listen(this.element.form, "reset", () => {
        this.resetTimeout = setTimeout(() => this.refresh(), 0)
      })
    }
    this.listen(document, "turbo:before-cache", () => this.disconnect())
    this.observer = new MutationObserver(() => this.refresh())
    this.observer.observe(this.element, { childList: true, subtree: true, attributes: true, attributeFilter: ["disabled", "selected"] })
    this.refresh()
    // Waits for the form's other controllers, which listen for the change it makes.
    if (this.pageUrlValue) this.resultTimeout = setTimeout(() => this.receivePageResult(), 0)
    if (this.inlineValue) this.open()
  }

  disconnect() {
    this.close(false, true)
    this.listeners?.abort()
    this.observer?.disconnect()
    clearTimeout(this.resetTimeout)
    clearTimeout(this.unlockTimeout)
    clearTimeout(this.resultTimeout)
    this.field?.remove()
    this.dialog?.remove()
    syncPageScrollLock()
    this.element.hidden = this.originalHidden
  }

  get searchUrl() { return this.searchUrlValue }

  // Only pickers whose results share one ID space can pick a pasted ID.
  get selectsPastedIds() { return false }

  get label() {
    return this.titleValue || this.element.getAttribute("aria-label") ||
      (this.element.getAttribute("aria-labelledby") || "").split(/\s+/).map(id => document.getElementById(id)?.textContent.trim()).filter(Boolean).join(" ") ||
      Array.from(this.element.labels, label => label.textContent.trim()).join(" ") ||
      this.element.getAttribute("placeholder") || "Choose an option"
  }

  get selectedOptions() {
    return Array.from(this.element.selectedOptions).filter(option => option.value !== "")
  }

  cloneTemplate(name) {
    return document.getElementById(`search-picker-${name}-template`).content.firstElementChild.cloneNode(true)
  }

  listen(element, event, callback, options = {}) {
    element.addEventListener(event, callback, { ...options, signal: this.listeners.signal })
  }

  // Touch devices open the list first so the keyboard doesn't cover it.
  get prefersKeyboard() {
    return window.matchMedia("(hover: hover) and (pointer: fine)").matches
  }

  refresh() {
    const selected = this.selectedOptions
    const placeholder = this.element.getAttribute("placeholder") ||
      Array.from(this.element.options).find(option => option.value === "")?.textContent.trim() || "Choose an option"
    const summary = this.element.multiple && selected.length ? `${selected.length} selected` :
      selected.map(option => option.textContent).join(", ") || placeholder
    this.field.querySelector("[data-picker-summary]").textContent = summary
    this.trigger.setAttribute("aria-label", `${this.label}${this.element.required ? " (required)" : ""}: ${summary}`)
    this.trigger.disabled = this.element.disabled
    this.chips.hidden = !this.element.multiple || selected.length === 0
    this.selection.hidden = !this.element.multiple
    this.hint.hidden = selected.length > 0
    this.hint.textContent = this.maxItemsValue ? `Choose up to ${this.maxItemsValue}` : "Nothing selected yet"
    this.renderChips(this.chips, this.element.multiple ? selected : [])
    this.renderChips(this.selection, this.element.multiple ? selected : [], this.hint)
    if (this.element.disabled) this.close()
    // An inline picker's page offers Clear in the native nav bar instead.
    this.clearButton.hidden = this.inlineValue || this.element.required || selected.length === 0
    this.updateResultSelection()
    this.updateStatus()
  }

  renderChips(container, options, ...keep) {
    container.replaceChildren(...keep)
    for (const option of options) {
      const chip = this.cloneTemplate("chip")
      chip.dataset.pickerValue = option.value
      chip.querySelector("[data-picker-label]").textContent = option.textContent
      chip.setAttribute("aria-label", `Remove ${option.textContent}`)
      chip.disabled = this.element.disabled || option.disabled
      container.append(chip)
    }
  }

  updateStatus(message) {
    if (message) {
      this.status.textContent = message
    } else if (this.element.multiple) {
      const count = this.selectedOptions.length
      this.status.textContent = this.maxItemsValue ? `${count} of ${this.maxItemsValue} selected` : `${count} selected`
    } else {
      this.status.textContent = ""
    }
    // Single choices close on selection, so only multiselects need a Done button.
    // An inline picker's page has its own, so its footer is only for messages.
    this.doneButton.hidden = !this.element.multiple || this.inlineValue
    this.footer.hidden = this.inlineValue ? !message && this.retryButton.hidden :
      !this.status.textContent && this.clearButton.hidden && this.retryButton.hidden && this.doneButton.hidden
  }

  open() {
    if (this.element.matches(":disabled") || this.dialog.open) return
    if (this.pageUrlValue) return this.openPage()
    this.search.value = ""
    this.searchClear.hidden = true
    this.updateStatus()
    clearTimeout(this.unlockTimeout)
    if (this.inlineValue) {
      this.dialog.show()
    } else {
      this.dialog.showModal()
    }
    syncPageScrollLock()
    this.trigger.setAttribute("aria-expanded", "true")
    this.viewportListener = () => this.resizeDialog()
    window.visualViewport?.addEventListener("resize", this.viewportListener)
    window.visualViewport?.addEventListener("scroll", this.viewportListener)
    this.resizeDialog()
    if (this.prefersKeyboard) this.search.focus({ preventScroll: true })
    this.loadResults()
  }

  close(restoreFocus = true, force = false) {
    // An inline picker is the page, so it stays open until the page goes.
    if (this.inlineValue && !force) return
    clearTimeout(this.searchTimeout)
    this.commitFirst = false
    this.pastedId = null
    window.visualViewport?.removeEventListener("resize", this.viewportListener)
    window.visualViewport?.removeEventListener("scroll", this.viewportListener)
    if (!this.dialog?.open) return
    this.dialog.close()
    // Bring the scrollbar back once the dialog has finished animating out.
    this.unlockTimeout = setTimeout(syncPageScrollLock, CLOSE_TRANSITION_MS)
    this.trigger.setAttribute("aria-expanded", "false")
    if (restoreFocus && this.trigger.isConnected) this.trigger.focus({ preventScroll: true })
  }

  // Native modals can't stack the dialog over their own sheet well, so the
  // picker opens as the next page in the modal (search_pickers/show). The form
  // is saved too: Android re-presents the sheet rather than popping back to it,
  // which reloads the page without the user's unsaved edits.
  openPage() {
    const config = Array.from(this.element.attributes)
      .filter(attribute => attribute.name.startsWith("data-search-picker-") && attribute.name !== "data-search-picker-page-url-value")
      .map(attribute => [attribute.name, attribute.value])
    const handoff = {
      returnUrl: location.href,
      name: this.element.name,
      multiple: this.element.multiple,
      placeholder: this.element.getAttribute("placeholder"),
      config,
      options: this.selectedOptions.map(option => ({ value: option.value, text: option.textContent })),
      form: this.element.form ? Array.from(new FormData(this.element.form)) : []
    }
    try {
      sessionStorage.setItem(PAGE_HANDOFF_KEY, JSON.stringify(handoff))
      sessionStorage.removeItem(PAGE_RESULT_KEY)
    } catch {
      return
    }
    const url = new URL(this.pageUrlValue, location.href)
    url.searchParams.set("title", this.label)
    Turbo.visit(url.href)
  }

  // Back from the picker's page: put the form back as it was when the page
  // opened, then apply the picked options as an ordinary change.
  receivePageResult() {
    // A preview is replaced by the real page, which receives the result instead.
    if (document.documentElement.hasAttribute("data-turbo-preview")) return
    const result = readStored(PAGE_RESULT_KEY)
    if (!result) return this.receivePageCancel()
    if (result.returnUrl !== location.href || result.name !== this.element.name) return
    sessionStorage.removeItem(PAGE_RESULT_KEY)

    if (this.element.form) restoreForm(this.element.form, result.form, this.element)
    for (const option of this.element.options) option.selected = false
    for (const { value, text } of result.options) {
      let option = Array.from(this.element.options).find(option => option.value === value)
      if (!option) {
        option = new Option(text, value)
        this.element.add(option)
      }
      option.selected = true
    }
    this.changed()
    // Bring back the field the picker was opened from, e.g. by reopening a
    // collapsed section around it, which a fresh load or snapshot resets.
    this.element.dispatchEvent(new CustomEvent("search-picker:returned", { bubbles: true }))
    this.revealField()
  }

  // Back from the picker's page without Done: the restored snapshot already
  // has the form as it was, but still needs the field brought back.
  receivePageCancel() {
    if (!restoredByVisit()) return
    const handoff = readStored(PAGE_HANDOFF_KEY)
    if (!handoff || handoff.returnUrl !== location.href || handoff.name !== this.element.name) return
    sessionStorage.removeItem(PAGE_HANDOFF_KEY)

    this.element.dispatchEvent(new CustomEvent("search-picker:returned", { bubbles: true }))
    this.revealField()
  }

  // Waits a few frames for whatever reopens around the field to lay it out.
  revealField(frames = 30) {
    if (!this.field.isConnected) return
    if (this.field.offsetParent || frames === 0) return this.field.scrollIntoView({ block: "center" })
    requestAnimationFrame(() => this.revealField(frames - 1))
  }

  resizeDialog() {
    const viewport = window.visualViewport
    this.dialog.style.setProperty("--picker-viewport-height", `${viewport?.height || window.innerHeight}px`)
    this.dialog.style.setProperty("--picker-viewport-top", `${viewport?.offsetTop || 0}px`)
  }

  scheduleSearch() {
    clearTimeout(this.searchTimeout)
    this.commitFirst = false
    this.searchClear.hidden = !this.search.value
    // Keep the current rows on screen until the new ones arrive, so the list
    // never flashes empty between keystrokes.
    this.results.setAttribute("aria-busy", "true")
    this.searchTimeout = setTimeout(() => this.loadResults(), this.searchUrl ? 200 : 0)
  }

  prefetch() {
    if (this.searchUrl && !this.pageUrlValue && !this.element.matches(":disabled")) this.fetchItems("").catch(() => {})
  }

  // Responses are cached per URL for the life of the picker, which makes
  // reopening and backspacing instant. Failures are dropped so a retry refetches.
  fetchItems(query) {
    const url = this.searchUrl + encodeURIComponent(query)
    if (!this.cache.has(url)) {
      const promise = fetch(url).then(response => {
        if (!response.ok) throw new Error("Search failed")
        return response.json()
      })
      promise.catch(() => this.cache.delete(url))
      this.cache.set(url, promise)
    }
    return this.cache.get(url)
  }

  localItems(query) {
    const normalize = text => text.normalize("NFD").replace(/\p{Diacritic}/gu, "").toLocaleLowerCase()
    const term = normalize(query)
    const items = Array.from(this.element.options).filter(option => option.value !== "").map(option => ({
      id: option.value, text: option.textContent, disabled: option.disabled || option.parentElement.disabled,
      heading: option.parentElement.tagName === "OPTGROUP" ? option.parentElement.label : ""
    })).filter(item => normalize(item.text).includes(term))
    if (this.sortFieldValue === "text") items.sort((a, b) => a.text.localeCompare(b.text))
    return items
  }

  async loadResults() {
    const token = Symbol("search")
    this.request = token
    const query = this.search.value.trim()
    this.retryButton.hidden = true
    this.retryButton.disabled = true
    this.results.setAttribute("aria-busy", "true")
    this.skeleton.hidden = this.results.childElementCount > 0
    try {
      const items = this.searchUrl ? await this.fetchItems(query) : this.localItems(query)
      if (this.request !== token || !this.dialog.open) return
      this.renderResults(items)
      this.renderEmpty(query, items.length === 0)
      if (this.failed) {
        this.failed = false
        this.refresh()
      }
      if (this.commitFirst) {
        this.commitFirst = false
        this.commitFirstResult()
      }
      if (this.pastedId === query) this.choosePastedId()
    } catch (error) {
      if (this.request !== token) return
      this.results.replaceChildren()
      this.empty.hidden = true
      this.failed = true
      this.clearButton.hidden = true
      this.retryButton.hidden = false
      this.updateStatus("Couldn't load results.")
    } finally {
      if (this.request === token) {
        this.results.setAttribute("aria-busy", "false")
        this.skeleton.hidden = true
        this.retryButton.disabled = false
        this.retryButton.dispatchEvent(new Event("loading-button:stop"))
      }
    }
  }

  renderResults(items) {
    this.items.clear()
    this.results.replaceChildren()
    this.results.scrollTop = 0
    const hasFlags = items.some(item => item.flag_path)
    for (const item of items) {
      const value = String(item.id)
      this.items.set(value, item)
      const row = this.cloneTemplate("option")
      row.dataset.pickerValue = value
      row.disabled = !!item.disabled
      // The pushed native picker page (search_pickers/show) is the only inline picker.
      if (this.inlineValue) row.classList.add("trigger-native-haptic")
      row.querySelector("[data-picker-label]").textContent = item.text
      const heading = row.querySelector("[data-picker-heading]")
      heading.textContent = item.heading || ""
      heading.hidden = !item.heading
      const detail = row.querySelector("[data-picker-detail]")
      detail.textContent = item.group ? "Group · all forms" : item.variant_type || ""
      detail.hidden = !detail.textContent
      row.querySelector("[data-picker-badge-label]").textContent = item.cruelty_free ? "Cruelty-free" : ""
      row.querySelector("[data-picker-badge]").hidden = !item.cruelty_free
      this.setImage(row.querySelector("[data-picker-image]"), item.thumbnail_url)
      row.querySelector("[data-picker-flag-slot]").hidden = !hasFlags
      this.setImage(row.querySelector("[data-picker-flag]"), item.flag_path)
      this.results.append(row)
    }
    this.updateResultSelection()
  }

  // Remote pickers only search once something is typed, so a blank search is a
  // prompt rather than a dead end.
  renderEmpty(query, visible) {
    this.empty.hidden = !visible
    if (!visible) return

    const prompt = !query && this.searchUrl
    const title = prompt ? "Start typing to search" : query ? `No results for “${query}”` : "No options available"
    const detail = prompt ? "Results appear as you type." : query ? "Check the spelling or try a shorter search." : ""
    this.empty.querySelector("[data-picker-empty-title]").textContent = title
    const detailElement = this.empty.querySelector("[data-picker-empty-detail]")
    detailElement.textContent = detail
    detailElement.hidden = !detail

    const action = this.empty.querySelector("[data-picker-empty-action]")
    const url = query && this.emptyActionUrlValue && this.safeUrl(this.emptyActionUrlValue)
    action.hidden = !url || !this.emptyActionLabelValue
    if (action.hidden) return
    const actionUrl = new URL(url)
    actionUrl.searchParams.set("name", query)
    action.href = actionUrl.href
    action.textContent = this.emptyActionLabelValue
  }

  setImage(image, value) {
    const url = this.safeUrl(value)
    if (url) image.src = url
    image.hidden = !url
  }

  updateResultSelection() {
    const values = new Set(this.selectedOptions.map(option => option.value))
    const full = this.element.multiple && this.maxItemsValue > 0 && values.size >= this.maxItemsValue
    for (const row of this.results.querySelectorAll("[data-picker-value]")) {
      const selected = values.has(row.dataset.pickerValue)
      row.setAttribute("aria-pressed", String(selected))
      // Rows past the limit only look unavailable, so choosing one explains why.
      row.toggleAttribute("data-picker-full", full && !selected)
      row.querySelector("[data-picker-checkbox]").hidden = !this.element.multiple
      row.querySelector("[data-picker-check]").hidden = this.element.multiple || !selected
    }
  }

  choose(value) {
    if (this.element.matches(":disabled")) return
    const item = this.items.get(value)
    if (!item || item.disabled) return
    if (this.navigateValue) {
      const url = this.safeUrl(item.url)
      if (url && new URL(url).origin === location.origin) Turbo.visit(url)
      return
    }
    let option = Array.from(this.element.options).find(option => option.value === value)
    if (this.element.multiple && option?.selected) return this.remove(value)
    if (this.element.multiple && this.maxItemsValue && this.selectedOptions.length >= this.maxItemsValue) {
      this.updateStatus(`Choose up to ${this.maxItemsValue}. Remove one to add another.`)
      return
    }
    if (!option) {
      option = new Option(item.text, value)
      this.element.add(option)
    }
    option.selected = true
    if (!this.element.multiple) this.close()
    this.changed()
  }

  remove(value) {
    for (const option of this.element.options) {
      if (option.value === value && !option.disabled) option.selected = false
    }
    this.changed()
  }

  clear() {
    this.element.value = ""
    this.changed()
  }

  changed() {
    this.element.dispatchEvent(new Event("input", { bubbles: true }))
    this.element.dispatchEvent(new Event("change", { bubbles: true }))
    this.refresh()
  }

  keydown(event) {
    if (event.isComposing) return

    // Keep the parent's modal Escape handlers from dismissing the underlying form.
    if (event.key === "Escape") {
      event.preventDefault()
      event.stopPropagation()
      this.close()
    } else if (["ArrowDown", "ArrowUp"].includes(event.key)) {
      const rows = Array.from(this.results.querySelectorAll("button:not(:disabled)"))
      if (!rows.length) return
      event.preventDefault()
      const index = rows.indexOf(document.activeElement)
      const next = event.key === "ArrowDown" ? (index + 1) % rows.length : (index <= 0 ? rows.length : index) - 1
      rows[next].focus()
    } else if (event.key.length === 1 && event.key !== " " && !event.metaKey && !event.ctrlKey && !event.altKey && event.target !== this.search && event.target.closest("[data-picker-results]")) {
      // Typing from a result row keeps searching instead of doing nothing.
      this.search.focus({ preventScroll: true })
    } else if (event.key === "Enter" && event.target === this.search) {
      event.preventDefault()
      // An onscreen keyboard's Search key means "show me the results", so it
      // only lowers the keyboard. Physical keyboards commit the top result.
      if (!this.prefersKeyboard) return this.search.blur()
      // Commit against the results for what was typed, not the rows still on screen.
      if (this.results.getAttribute("aria-busy") === "true") this.commitFirst = true
      else this.commitFirstResult()
    }
  }

  // Pasting a bare ID picks the result with that ID, so
  // admins can link records without searching by name.
  pasted(event) {
    const text = event.clipboardData?.getData("text").trim() || ""
    this.pastedId = this.selectsPastedIds && /^\d+$/.test(text) ? text : null
  }

  choosePastedId() {
    const value = this.pastedId
    this.pastedId = null
    const row = Array.from(this.results.querySelectorAll("[data-picker-value]")).find(row => row.dataset.pickerValue === value)
    if (row && !row.disabled && row.getAttribute("aria-pressed") !== "true") row.click()
  }

  // Enter only ever adds, so pressing it again can't drop a multiselect choice.
  commitFirstResult() {
    const row = this.results.querySelector("button:not(:disabled)")
    if (this.element.multiple && row?.getAttribute("aria-pressed") === "true") return
    row?.click()
  }

  safeUrl(value) {
    if (!value) return null
    try {
      const url = new URL(value, location.href)
      return ["http:", "https:"].includes(url.protocol) ? url.href : null
    } catch {
      return null
    }
  }
}

// Sets each control (other than `skip`) back to the entries FormData read from
// the form, announcing the ones that change so their controllers catch up.
function restoreForm(form, entries, skip) {
  const values = new Map()
  for (const [name, value] of entries) values.set(name, [...(values.get(name) || []), value])
  const position = new Map()
  for (const control of form.elements) {
    if (!control.name || control === skip || control.disabled || ["button", "submit", "reset", "file"].includes(control.type)) continue
    const wanted = values.get(control.name) || []
    let changed = false
    if (control.type === "checkbox" || control.type === "radio") {
      const checked = wanted.includes(control.value)
      changed = control.checked !== checked
      control.checked = checked
    } else if (control.tagName === "SELECT") {
      for (const option of control.options) {
        const selected = wanted.includes(option.value)
        changed ||= option.selected !== selected
        option.selected = selected
      }
    } else {
      const index = position.get(control.name) || 0
      position.set(control.name, index + 1)
      if (index < wanted.length && control.value !== wanted[index]) {
        control.value = wanted[index]
        changed = true
      }
    }
    if (changed) {
      control.dispatchEvent(new Event("input", { bubbles: true }))
      control.dispatchEvent(new Event("change", { bubbles: true }))
    }
  }
}
